if (!customElements.get('ff-available-products')) {
  class FFAvailableProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.grid = this.querySelector('[data-ff-product-grid]');
      this.controls = this.querySelector('[data-ff-controls]');
      this.button = this.querySelector('[data-ff-load-more]');
      this.status = this.querySelector('[data-ff-status]');
      if (!this.grid || !this.controls || !this.button || !this.status || !this.dataset.sectionId) return;

      this.initialized = true;
      this.pageSize = Math.max(1, Number.parseInt(this.dataset.pageSize, 10) || 16);
      this.maxItems = Math.max(0, Number.parseInt(this.dataset.maxItems, 10) || 0);
      this.baseUrl = new URL(window.location.href);
      this.nextUrl = this.dataset.nextUrl || '';
      this.buffer = [];
      this.seenProducts = new Set();
      this.visitedPages = new Set();
      this.shown = 0;
      this.busy = false;
      this.failed = false;
      this.paused = false;
      this.onQueryStart = (event) => {
        if (!this.closest('#ProductGridContainer')) return;
        if (event.detail?.sectionId && event.detail.sectionId !== this.dataset.sectionId) return;
        this.paused = true;
        this.requestController?.abort();
        this.updateState();
      };
      this.onQueryError = (event) => {
        if (event.detail?.sectionId && event.detail.sectionId !== this.dataset.sectionId) return;
        this.paused = false;
        this.updateState();
      };
      document.addEventListener('ff:collection-query-start', this.onQueryStart);
      document.addEventListener('ff:collection-query-error', this.onQueryError);

      const firstTarget = this.maxItems ? Math.min(this.pageSize, this.maxItems) : this.pageSize;
      Array.from(this.grid.children).forEach((card) => {
        if (!card.dataset.productId) return;
        if (this.seenProducts.has(card.dataset.productId)) {
          card.remove();
          return;
        }
        this.seenProducts.add(card.dataset.productId);
        if (this.shown < firstTarget) {
          this.shown += 1;
        } else {
          this.buffer.push(card);
          card.remove();
        }
      });

      this.button.addEventListener('click', () => this.load());
      this.controls.hidden = false;
      this.querySelectorAll('[data-ff-pagination]').forEach((element) => {
        element.hidden = true;
      });
      this.updateState();
      if (this.shown < firstTarget && this.hasMore()) this.load(firstTarget);
    }

    disconnectedCallback() {
      this.requestController?.abort();
      document.removeEventListener('ff:collection-query-start', this.onQueryStart);
      document.removeEventListener('ff:collection-query-error', this.onQueryError);
    }

    text(key, fallback) {
      const value = this.dataset[`text${key}`];
      return value && !value.toLowerCase().includes('translation missing') ? value : fallback;
    }

    hasMore() {
      return (!this.maxItems || this.shown < this.maxItems) && (this.buffer.length > 0 || Boolean(this.nextUrl));
    }

    updateState(message = '') {
      this.button.disabled = this.busy || this.paused;
      this.button.setAttribute('aria-busy', String(this.busy || this.paused));
      this.grid.setAttribute('aria-busy', String(this.busy || this.paused));
      this.button.hidden = !this.busy && !this.paused && !this.hasMore();
      this.button.style.display = this.button.hidden ? 'none' : '';
      this.button.textContent = this.busy || this.paused
        ? this.text('Loading', 'Loading products…')
        : this.failed
          ? this.text('Retry', 'Try again')
          : this.text('LoadMore', 'Load more');
      const count = this.text('Shown', 'Products shown: __COUNT__').replace('__COUNT__', String(this.shown));
      this.status.textContent = (this.paused ? this.text('Loading', 'Loading products…') : message) || (this.shown ? count : this.hasMore()
        ? this.text('Pending', 'More products may be available. Select Load more to keep looking.')
        : this.text('None', 'No available products found.'));
      if (!this.nextUrl && !this.buffer.length && this.dataset.paginationLimited === 'true') {
        this.status.textContent += ` ${this.text('Narrow', 'Explore a subcategory or use search to find more products.')}`;
      }
      this.querySelectorAll('[data-ff-empty]').forEach((element) => {
        element.hidden = this.shown > 0 || this.hasMore() || this.busy;
      });
      this.updateSlider();
    }

    updateSlider() {
      const slider = this.querySelector('slider-component');
      const controls = this.querySelector('[data-ff-slider-controls]');
      if (!slider || !controls) return;
      if (!slider.ffAvailableInitPagesPatched && typeof slider.initPages === 'function') {
        const originalInitPages = slider.initPages;
        slider.initPages = function (...args) {
          originalInitPages.apply(this, args);
          if (this.sliderItemsToShow?.length >= 2 && Number.isFinite(this.totalPages) && this.totalPages < 1) {
            this.totalPages = 1;
            this.update?.();
          }
        };
        slider.ffAvailableInitPagesPatched = true;
      }
      controls.hidden = this.shown < 2;
      controls.style.display = controls.hidden ? 'none' : '';
      if (controls.hidden) {
        const current = controls.querySelector('.slider-counter--current');
        const total = controls.querySelector('.slider-counter--total');
        if (current) current.textContent = String(this.shown);
        if (total) total.textContent = String(this.shown);
        controls.querySelectorAll('button').forEach((button) => {
          button.disabled = true;
        });
        return;
      }
      slider.resetPages?.();
    }

    appendBuffered(target) {
      const fragment = document.createDocumentFragment();
      let added = 0;
      while (this.buffer.length && this.shown < target) {
        const card = this.buffer.shift();
        card.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--slide-in');
        card.removeAttribute('data-cascade');
        card.style.removeProperty('--animation-order');
        fragment.appendChild(card);
        this.shown += 1;
        added += 1;
      }
      if (added) {
        this.grid.appendChild(fragment);
        this.querySelector('slider-component')?.resetPages?.();
        this.dispatchEvent(new CustomEvent('ff:products-added', { bubbles: true, detail: { count: added } }));
      }
    }

    requestUrl(nextUrl) {
      const url = new URL(nextUrl, this.baseUrl);
      if (url.origin !== this.baseUrl.origin || url.pathname !== this.baseUrl.pathname) {
        throw new Error('Invalid collection pagination URL.');
      }
      for (const key of ['preview_theme_id', '_fd', '_sc']) {
        if (url.searchParams.has(key)) continue;
        for (const value of this.baseUrl.searchParams.getAll(key)) url.searchParams.append(key, value);
      }
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url;
    }

    async fetchNextPage() {
      const url = this.requestUrl(this.nextUrl);
      if (this.visitedPages.has(url.href)) throw new Error('Repeated collection pagination URL.');
      this.requestController = new AbortController();
      const timeout = window.setTimeout(() => this.requestController.abort(), 12000);
      try {
        const response = await fetch(url.href, {
          credentials: 'same-origin',
          signal: this.requestController.signal,
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error(`Products request failed: ${response.status}`);
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = Array.from(documentResult.querySelectorAll('ff-available-products'))
          .find((element) => element.dataset.sectionId === this.dataset.sectionId);
        const sourceGrid = source?.querySelector('[data-ff-product-grid]');
        if (!sourceGrid) throw new Error('Product list missing from section response.');
        if (!this.isConnected || this.paused) return;
        for (const card of Array.from(sourceGrid.children)) {
          if (!card.dataset.productId || this.seenProducts.has(card.dataset.productId)) continue;
          this.seenProducts.add(card.dataset.productId);
          this.buffer.push(document.importNode(card, true));
        }
        this.visitedPages.add(url.href);
        this.nextUrl = source.dataset.nextUrl || '';
      } finally {
        window.clearTimeout(timeout);
        this.requestController = null;
      }
    }

    async load(initialTarget) {
      if (this.busy || this.paused || !this.hasMore() || !this.isConnected) return;
      let target = initialTarget || this.shown + this.pageSize;
      if (this.maxItems) target = Math.min(target, this.maxItems);
      this.busy = true;
      this.failed = false;
      this.updateState(this.text('Loading', 'Loading products…'));
      let requests = 0;
      try {
        this.appendBuffered(target);
        while (this.shown < target && this.nextUrl && requests < 8 && this.isConnected && !this.paused) {
          requests += 1;
          await this.fetchNextPage();
          if (!this.isConnected || this.paused) return;
          this.appendBuffered(target);
        }
      } catch (error) {
        if (!this.isConnected || this.paused) return;
        this.failed = true;
      } finally {
        this.busy = false;
        if (this.isConnected) {
          const message = this.failed
            ? this.text('Error', 'Products could not be loaded. Please try again.')
            : this.shown < target && this.hasMore()
              ? this.text('Pending', 'More products may be available. Select Load more to keep looking.')
              : '';
          this.updateState(message);
        }
      }
    }
  }

  customElements.define('ff-available-products', FFAvailableProducts);
}
